import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from '../utils/toastUtils';
import { CartStorage } from '../utils/localStorage';

const CartContext = createContext();

const initialState = {
  items: [],
  isOpen: false,
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART':
      return { ...state, items: action.payload };

    case 'ADD_TO_CART': {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex(item => item.id === newItem.id);
      let updatedItems;

      if (existingItemIndex >= 0) {
        updatedItems = [...state.items];
        const existingItem = updatedItems[existingItemIndex];
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + newItem.quantity,
        };
      } else {
        updatedItems = [...state.items, newItem];
      }

      return { ...state, items: updatedItems };
    }

    case 'REMOVE_FROM_CART': {
      return { ...state, items: state.items.filter(item => item.id !== action.payload) };
    }

    case 'UPDATE_QUANTITY': {
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    }

    case 'CLEAR_CART': {
      return { ...state, items: [] };
    }

    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };

    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount (only once)
  useEffect(() => {
    const cartItems = CartStorage.getItems();
    if (cartItems.length > 0) {
      dispatch({ type: 'LOAD_CART', payload: cartItems });
    }
  }, []);

  useEffect(() => {
    if (state.items) {
      CartStorage.setItems(state.items);
    }
  }, [state.items]);

  const addToCart = (product, quantity = 1, size = null) => {
    if (!product.inStock || product.stockQuantity <= 0) {
      toast.error(`${product.name} is currently out of stock`, {
        id: 'cart-error'
      });
      return;
    }

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        id: size ? `${product._id}-${size}` : product._id,
        productId: product._id, // Keep original reference
        name: product.name + (size ? ` (${size})` : ''), // Visual distinction
        price: product.price,
        image: (product.images?.[0]?.url?.length > 100000)
          ? 'https://via.placeholder.com/400x400?text=Image+Saved'
          : product.images?.[0]?.url,
        quantity,
        inStock: product.inStock,
        stockQuantity: product.stockQuantity,
        subscription: product.subscription,
        category: product.category, // Add category for postcard detection
        size: size,
        customBouquet: product.customBouquet, // Preserve custom bouquet details
      },
    });
    // Use a specific ID to prevent stacking multiple "Added to cart" messages
    toast.success(`${product.name} ${size ? `(${size}) ` : ''}added to cart!`, {
      id: 'cart-update'
    });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
    toast.success('Item removed from cart', { id: 'cart-update' });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id: productId, quantity },
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast.success('Cart cleared', { id: 'cart-update' });
  };

  // Note: clearUserCart removed as we no longer have user-specific carts

  const toggleCart = () => {
    dispatch({ type: 'TOGGLE_CART' });
  };

  const setCartOpen = (isOpen) => {
    dispatch({ type: 'SET_CART_OPEN', payload: isOpen });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getCartItem = (productId) => {
    return state.items.find(item => item.id === productId);
  };

  const value = React.useMemo(() => ({
    items: state.items,
    isOpen: state.isOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    setCartOpen,
    getCartTotal,
    getCartItemsCount,
    isInCart,
    getCartItem,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state.items, state.isOpen]); // Only re-create when items or open state change

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
